import { useCallback, useMemo } from 'react';
import { GraphQLFetchPolicies } from 'shared/enums/GraphQLFetchPolicies';
import { SidePanelParentPetDataFragment, SortOrder, useGetSidePanelParentPetsQuery } from 'shared/types/graphql';

export const useGetParentPets = (
  petParentId?: string,
  activeStatus?: boolean,
): {
  pets: SidePanelParentPetDataFragment[];
  petCount: number;
  fetchMore: () => Promise<void>;
  refetchPets: () => Promise<void>;
  loading: boolean;
} => {
  const take = 10;
  const includeActiveFilterIfProvided = {
    ...(activeStatus === undefined ? {} : { isActive: { equals: activeStatus } }),
  };

  const {
    data: petData,
    fetchMore: fetchMorePets,
    loading: isPetDataLoading,
    refetch: refetchPetData,
  } = useGetSidePanelParentPetsQuery({
    variables: {
      where: {
        isDeleted: { equals: false },
        ...includeActiveFilterIfProvided,
        // TODO: we removed filtering for deceased and inactive patients but for performance reasons we may want to consider adding this back on demand
        petParents: {
          some: {
            id: {
              equals: petParentId,
            },
          },
        },
      },
      take,
      orderBy: [{ isActive: SortOrder.Desc }, { name: SortOrder.Asc }],
    },
    skip: !petParentId,
    fetchPolicy: GraphQLFetchPolicies.CacheAndNetwork,
    nextFetchPolicy: GraphQLFetchPolicies.CacheFirst,
  });

  const pets = useMemo((): SidePanelParentPetDataFragment[] => {
    return petData?.findManyClinicPet || [];
  }, [petData]);

  const petCount = petData?.findManyClinicPetCount || 0;

  const loadMorePets = useCallback(async () => {
    if (petParentId && fetchMorePets) {
      await fetchMorePets({
        variables: {
          take: take + pets.length,
        },
      });
    }
  }, [petParentId, fetchMorePets, pets.length, take]);

  return {
    pets,
    petCount,
    fetchMore: async (): Promise<void> => {
      if (!petParentId) return;
      await loadMorePets();
    },
    refetchPets: async (): Promise<void> => {
      if (!petParentId) return;
      await refetchPetData();
    },
    loading: isPetDataLoading,
  };
};
